import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "./BaseUrl";
import InnerHeader from "./InnerHeader";
import Loader from "./Loader";

const ManualAdmission = () => {
    const navigate = useNavigate();
    const { inquiryid } = useParams();

    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("Personal"); // 'Personal' | 'Education' | 'Work' | 'Training Programme Selection' | 'Documents'
    const [savedTabs, setSavedTabs] = useState({
        Personal: false,
        Education: false,
        Work: false,
        "Training Programme Selection": false,
        Documents: false
    });

    // Lookup / Dropdown States
    const [courses, setCourses] = useState([]);
    const [allBatches, setAllBatches] = useState([]);
    const [filteredBatches, setFilteredBatches] = useState([]);
    const [educations, setEducations] = useState([]);
    const [disciplines, setDisciplines] = useState([]);
    const [colleges, setColleges] = useState([]);
    const [batchCategories, setBatchCategories] = useState([]);

    const [sameAddress, setSameAddress] = useState(false);
    const [showNewCollegeInput, setShowNewCollegeInput] = useState(false);
    const [errors, setErrors] = useState({});
    const [successModal, setSuccessModal] = useState(null);

    // Master Form State covering all 5 steps
    const [formData, setFormData] = useState({
        // Tab 1: Personal (addmission_form.php)
        Student_Id: "",
        applicant_name: "",
        id_card_name: "",
        dob_dd: "",
        gender: "Male",
        email: "",
        mobile: "",
        telephone: "",
        FemilyNo: "",
        nationality: "Indian",
        present_address: "",
        present_city: "",
        present_pin: "",
        present_state: "",
        present_country: "India",
        permanent_address: "",
        permanent_city: "",
        permanent_pin: "",
        permanent_state: "",
        permanent_country: "India",
        Inquiry_Id: inquiryid && inquiryid !== ":inquiryid" ? inquiryid : "",

        // Tab 2: Education (education_form.php)
        degree: "",
        discipline: "",
        college: "",
        college_data_value: "",
        university: "",
        year_of_passing: "",
        marks: "",
        status: "",
        backlogs: "0",
        remark: "",

        // Tab 3: Work (work.php)
        Occupation: "1", // 1 = Student, 2 = Employee, 3 = Self Employee
        company_name: "",
        discription: "",
        designation: "",
        working_years: "0",
        working_months: "0",
        self_employment_details: "",
        sworking_years: "0",
        sworking_months: "0",

        // Tab 4: Training Programme Selection (Programme.php)
        training_programme: "", // Course_Id
        Course_Id: "",
        category: "", // Batch_Category_id
        category_id: "",
        Batch_code: "",
        Batch_Id: "",
        Admission_Dt: new Date().toISOString().slice(0, 10),
        Amount: "",
        Payment_Type: "Lumpsum",
        terms_conditions: false,

        // Tab 5: Documents (documents.php)
        photo_id_proof: "",
        address_proof: "",
        student_code: "",
        admission_id: ""
    });

    // File objects state for Tab 5 upload
    const [documentFiles, setDocumentFiles] = useState({
        photo: null,
        certificate: null,
        marksheet: null,
        photo_id_proof_file: null,
        address_proof_file: null
    });

    const [uploadStatus, setUploadStatus] = useState({
        photo: false,
        certificate: false,
        marksheet: false,
        photo_id_proof_file: false,
        address_proof_file: false
    });

    // 15 years ago date limit for DOB
    const today = new Date();
    const maxDobDate = new Date(today.getFullYear() - 15, today.getMonth(), today.getDate())
        .toISOString()
        .split("T")[0];

    // Fetch initial Dropdowns data
    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                setLoading(true);
                const [
                    coursesRes,
                    batchesRes,
                    eduRes,
                    discRes,
                    collegeRes,
                    catRes
                ] = await Promise.allSettled([
                    axios.get(`${BASE_URL}/getCourse`),
                    axios.get(`${BASE_URL}/getBtach`),
                    axios.get(`${BASE_URL}/getEducation`),
                    axios.get(`${BASE_URL}/getDiscipline`),
                    axios.get(`${BASE_URL}/getCollege`),
                    axios.get(`${BASE_URL}/getBtachCategory`)
                ]);

                if (coursesRes.status === "fulfilled" && Array.isArray(coursesRes.value.data)) {
                    setCourses(coursesRes.value.data);
                }
                if (batchesRes.status === "fulfilled" && Array.isArray(batchesRes.value.data)) {
                    setAllBatches(batchesRes.value.data);
                    setFilteredBatches(batchesRes.value.data);
                }
                if (eduRes.status === "fulfilled" && Array.isArray(eduRes.value.data)) {
                    setEducations(eduRes.value.data);
                }
                if (discRes.status === "fulfilled" && Array.isArray(discRes.value.data)) {
                    setDisciplines(discRes.value.data);
                }
                if (collegeRes.status === "fulfilled" && Array.isArray(collegeRes.value.data)) {
                    setColleges(collegeRes.value.data);
                }
                if (catRes.status === "fulfilled" && Array.isArray(catRes.value.data)) {
                    setBatchCategories(catRes.value.data);
                }
            } catch (err) {
                console.error("Error loading dropdown data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchDropdownData();
    }, []);

    // Prefill if inquiryid is provided
    useEffect(() => {
        if (inquiryid && inquiryid !== ":inquiryid") {
            const fetchInquiry = async () => {
                try {
                    setLoading(true);
                    const res = await axios.post(`${BASE_URL}/studentDetail`, { id: inquiryid });
                    if (res.data && res.data[0]) {
                        const inq = res.data[0];
                        let formattedDob = "";
                        if (inq.DOB) {
                            try {
                                formattedDob = new Date(inq.DOB).toISOString().slice(0, 10);
                            } catch (e) {
                                formattedDob = inq.DOB;
                            }
                        }

                        setFormData((prev) => ({
                            ...prev,
                            applicant_name: inq.Student_Name || "",
                            dob_dd: formattedDob,
                            gender: inq.Sex || "Male",
                            email: inq.Email || "",
                            mobile: inq.Present_Mobile || "",
                            nationality: inq.Nationality || "Indian",
                            Course_Id: inq.Course_Id || "",
                            training_programme: inq.Course_Id || "",
                            Inquiry_Id: inquiryid
                        }));
                    }
                } catch (err) {
                    console.error("Error fetching inquiry:", err);
                } finally {
                    setLoading(false);
                }
            };

            fetchInquiry();
        }
    }, [inquiryid]);

    // Filter Batches whenever Course or Category changes
    useEffect(() => {
        let list = [...allBatches];
        const selectedCourse = formData.Course_Id || formData.training_programme;
        const selectedCategory = formData.category_id || formData.category;

        if (selectedCourse) {
            list = list.filter((b) => String(b.Course_Id) === String(selectedCourse));
        }
        if (selectedCategory) {
            list = list.filter((b) => String(b.Batch_Category_id) === String(selectedCategory));
        }

        setFilteredBatches(list.length > 0 ? list : (selectedCourse ? list : allBatches));
    }, [formData.Course_Id, formData.training_programme, formData.category_id, formData.category, allBatches]);

    // Handle standard inputs
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let val = type === "checkbox" ? checked : value;

        if (["mobile", "telephone", "FemilyNo"].includes(name)) {
            val = String(val || "").replace(/\D/g, "").slice(0, 10);
        }

        setFormData((prev) => {
            const updated = { ...prev, [name]: val };

            // Sync Course_Id and training_programme
            if (name === "training_programme") {
                updated.Course_Id = val;
            } else if (name === "Course_Id") {
                updated.training_programme = val;
            }

            // Sync category and category_id
            if (name === "category") {
                updated.category_id = val;
            }

            // Sync present address to permanent if sameAddress is true
            if (sameAddress && name.startsWith("present_")) {
                const perm = name.replace("present_", "permanent_");
                updated[perm] = val;
            }

            return updated;
        });

        // Clear error on type
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    // Handle College selection
    const handleCollegeChange = (e) => {
        const val = e.target.value;
        if (val === "ADDTHIS") {
            setShowNewCollegeInput(true);
            setFormData((prev) => ({ ...prev, college: "ADDTHIS" }));
        } else {
            setShowNewCollegeInput(false);
            setFormData((prev) => ({ ...prev, college: val, college_data_value: "" }));
        }
        if (errors.college) setErrors((prev) => ({ ...prev, college: null }));
    };

    // Handle Batch Change and auto-fetch Fee
    const handleBatchChange = async (e) => {
        const batchCode = e.target.value;
        const selectedBatch = allBatches.find((b) => String(b.Batch_code) === String(batchCode));
        const batchId = selectedBatch ? selectedBatch.Batch_Id : "";

        setFormData((prev) => ({
            ...prev,
            Batch_code: batchCode,
            Batch_Id: batchId
        }));

        if (errors.Batch_code) setErrors((prev) => ({ ...prev, Batch_code: null }));

        if (batchCode) {
            try {
                const res = await axios.post(`${BASE_URL}/getBtachwiseamount`, { Batch_Code: batchCode });
                if (res.data && res.data[0] && res.data[0].total_inr) {
                    setFormData((prev) => ({
                        ...prev,
                        Amount: res.data[0].total_inr
                    }));
                }
            } catch (err) {
                console.error("Failed to load batch fees:", err);
            }
        }
    };

    // Toggle same address
    const handleSameAddressToggle = (e) => {
        const checked = e.target.checked;
        setSameAddress(checked);
        if (checked) {
            setFormData((prev) => ({
                ...prev,
                permanent_address: prev.present_address,
                permanent_city: prev.present_city,
                permanent_pin: prev.present_pin,
                permanent_state: prev.present_state,
                permanent_country: prev.present_country
            }));
        }
    };

    // File selection handler with 1MB limit check
    const handleFileChange = (e, fieldName) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        const maxSize = 1 * 1024 * 1024; // 1MB
        if (file.size > maxSize) {
            alert(`File size must be less than 1MB. Selected file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`);
            e.target.value = "";
            return;
        }

        setDocumentFiles((prev) => ({ ...prev, [fieldName]: file }));
        setUploadStatus((prev) => ({ ...prev, [fieldName]: true }));
    };

    // Tab 1 Validation (Personal)
    const validateTab1 = () => {
        const newErrors = {};
        if (!formData.applicant_name.trim()) {
            newErrors.applicant_name = "Enter Name (To be printed on Certificate) is required.";
        }
        if (!formData.dob_dd) {
            newErrors.dob_dd = "Date of Birth is required.";
        } else if (new Date(formData.dob_dd) > new Date(maxDobDate)) {
            newErrors.dob_dd = "Applicant must be at least 15 years old.";
        }
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
            newErrors.email = "Email Address is required.";
        } else if (!emailPattern.test(formData.email.trim())) {
            newErrors.email = "Please enter a valid email address.";
        }
        const mobilePattern = /^[6-9]\d{9}$/;
        if (!formData.mobile.trim()) {
            newErrors.mobile = "Mobile Number is required.";
        } else if (!mobilePattern.test(formData.mobile.trim())) {
            newErrors.mobile = "Please enter a valid 10-digit mobile number starting with 6-9.";
        }
        if (formData.telephone.trim() && formData.telephone.trim().length !== 10) {
            newErrors.telephone = "Telephone Number must be 10 digits.";
        }
        if (formData.FemilyNo.trim() && formData.FemilyNo.trim().length !== 10) {
            newErrors.FemilyNo = "Family Number must be 10 digits.";
        } else if (formData.FemilyNo.trim() && formData.mobile.trim() === formData.FemilyNo.trim()) {
            newErrors.FemilyNo = "Family Number cannot be the same as Mobile Number.";
        }
        if (!formData.nationality.trim()) {
            newErrors.nationality = "Nationality is required.";
        }
        if (!formData.present_address.trim()) newErrors.present_address = "Present Address is required.";
        if (!formData.present_city.trim()) newErrors.present_city = "City is required.";
        if (!formData.present_pin.trim()) newErrors.present_pin = "Pin Code is required.";
        if (!formData.present_state.trim()) newErrors.present_state = "State is required.";
        if (!formData.present_country.trim()) newErrors.present_country = "Country is required.";
        if (!formData.permanent_address.trim()) newErrors.permanent_address = "Permanent Address is required.";
        if (!formData.permanent_city.trim()) newErrors.permanent_city = "City is required.";
        if (!formData.permanent_pin.trim()) newErrors.permanent_pin = "Pin Code is required.";
        if (!formData.permanent_state.trim()) newErrors.permanent_state = "State is required.";
        if (!formData.permanent_country.trim()) newErrors.permanent_country = "Country is required.";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Tab 2 Validation (Education)
    const validateTab2 = () => {
        const newErrors = {};
        if (!formData.degree) newErrors.degree = "Degree is required.";
        if (!formData.discipline) newErrors.discipline = "Discipline is required.";
        if (!formData.college) newErrors.college = "College is required.";
        if (formData.college === "ADDTHIS" && !formData.college_data_value.trim()) {
            newErrors.college_data_value = "Please enter the college name.";
        }
        if (!formData.university.trim()) newErrors.university = "University is required.";
        if (!formData.year_of_passing.trim()) newErrors.year_of_passing = "Year of passing is required.";
        if (!formData.marks.trim()) newErrors.marks = "Marks / Percentage is required.";
        if (!formData.status) newErrors.status = "Status is required.";
        if (formData.backlogs === "" || formData.backlogs === null) newErrors.backlogs = "Backlogs field is required.";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Tab 3 Validation (Work)
    const validateTab3 = () => {
        const newErrors = {};
        if (!formData.Occupation) newErrors.Occupation = "Occupation is required.";
        if (formData.Occupation === "2") {
            if (!formData.company_name.trim()) newErrors.company_name = "Job Organisation is required.";
            if (!formData.designation.trim()) newErrors.designation = "Designation is required.";
        }
        if (formData.Occupation === "3") {
            if (!formData.self_employment_details.trim()) {
                newErrors.self_employment_details = "Details of Self Employment is required.";
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Tab 4 Validation (Training Programme Selection)
    const validateTab4 = () => {
        const newErrors = {};
        if (!formData.training_programme && !formData.Course_Id) {
            newErrors.training_programme = "Training Programme is required.";
        }
        if (!formData.category && !formData.category_id) {
            newErrors.category = "Category is required.";
        }
        if (!formData.Batch_code) {
            newErrors.Batch_code = "Batch Code is required.";
        }
        if (!formData.terms_conditions) {
            newErrors.terms_conditions = "You must read and agree to all terms & conditions.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // NEXT Tab Handler (Validates current step and switches to next tab - NO DB INSERTION)
    const handleNextTab = (currentTabName, nextTabName) => {
        if (currentTabName === "Personal" && !validateTab1()) {
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        if (currentTabName === "Education" && !validateTab2()) {
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        if (currentTabName === "Work" && !validateTab3()) {
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        if (currentTabName === "Training Programme Selection" && !validateTab4()) {
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        // Mark current tab as verified/completed in state
        setSavedTabs((prev) => ({ ...prev, [currentTabName]: true }));

        // Advance to next tab
        if (nextTabName) {
            setActiveTab(nextTabName);
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    // FINAL SUBMISSION (Only executed on Tab 5 - Documents):
    // Inserts all values of the form into the database in one single complete submission, then uploads files!
    const handleFinalSubmit = async (e) => {
        e.preventDefault();

        // 1. Comprehensive pre-submission checks
        if (!validateTab1()) {
            setActiveTab("Personal");
            alert("Please review and complete the required Personal Information.");
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        if (!validateTab2()) {
            setActiveTab("Education");
            alert("Please review and complete the required Academic Qualification fields.");
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        if (!validateTab3()) {
            setActiveTab("Work");
            alert("Please review and complete the required Work details.");
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        if (!validateTab4()) {
            setActiveTab("Training Programme Selection");
            alert("Please review and select the Training Programme and Batch.");
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        try {
            setLoading(true);

            // 2. Insert all admission data into the database in one single request
            const res = await axios.post(`${BASE_URL}/addManualAdmission`, formData);

            if (res.data && res.data.success) {
                const studentId = res.data.student_id;
                const studentCode = res.data.student_code;

                setFormData((prev) => ({
                    ...prev,
                    Student_Id: studentId,
                    student_code: studentCode,
                    admission_id: res.data.admission_id
                }));

                // 3. Upload any selected document files with the newly created student_id
                const uploadTasks = [];
                const docMappings = [
                    { key: "photo", doc_name: "photo" },
                    { key: "certificate", doc_name: "certificate" },
                    { key: "marksheet", doc_name: "marksheet" },
                    { key: "photo_id_proof_file", doc_name: formData.photo_id_proof || "photo_id_proof" },
                    { key: "address_proof_file", doc_name: formData.address_proof || "address_proof" }
                ];

                for (const item of docMappings) {
                    const fileObj = documentFiles[item.key];
                    if (fileObj) {
                        const fd = new FormData();
                        fd.append("image", fileObj);
                        fd.append("doc_name", item.doc_name);
                        fd.append("student_id", studentId);
                        uploadTasks.push(
                            axios.post(`${BASE_URL}/upload_doc`, fd, {
                                headers: { "Content-Type": "multipart/form-data" }
                            })
                        );
                    }
                }

                if (uploadTasks.length > 0) {
                    await Promise.allSettled(uploadTasks);
                }

                setSavedTabs({
                    Personal: true,
                    Education: true,
                    Work: true,
                    "Training Programme Selection": true,
                    Documents: true
                });

                // 4. Open Success Modal
                setSuccessModal({
                    student_id: studentId,
                    student_code: studentCode || "Generated",
                    student_name: formData.applicant_name,
                    course: courses.find((c) => String(c.Course_Id) === String(formData.Course_Id))?.Course_Name || "Course Enrolled",
                    batch: formData.Batch_code || "N/A"
                });
            } else {
                alert(res.data?.message || "Failed to save admission. Please verify all details.");
            }
        } catch (err) {
            console.error("Admission submission error:", err);
            const msg = err.response?.data?.message || err.message || "Failed to submit admission.";
            alert(`Error submitting admission: ${msg}`);
        } finally {
            setLoading(false);
        }
    };

    const tabList = [
        { id: "Personal", label: "1. Personal" },
        { id: "Education", label: "2. Education" },
        { id: "Work", label: "3. Work" },
        { id: "Training Programme Selection", label: "4. Training Programme Selection" },
        { id: "Documents", label: "5. Documents" }
    ];

    return (
        <div className="container-fluid page-body-wrapper">
            <InnerHeader />
            {loading && <Loader />}

            <div className="main-panel">
                <div className="content-wrapper">
                    {/* Page Header */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h3 className="font-weight-bold text-primary mb-1"> Admission Form</h3>
                            <p className="text-muted mb-0">
                                Fill each section and submit once at the Documents step.
                            </p>
                        </div>
                        <div>
                            <Link to="/admissionlisting" className="btn btn-outline-secondary">
                                &larr; Back to Admission List
                            </Link>
                        </div>
                    </div>

                    {/* Multi-Tab Navigation Bar matching project styling & hearder.php */}
                    <div className="card mb-4 shadow-sm" style={{ borderRadius: "8px" }}>
                        <div className="card-header p-0" style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                            <ul className="nav nav-tabs card-header-tabs m-0" style={{ borderBottom: "none" }}>
                                {tabList.map((tab) => {
                                    const isActive = activeTab === tab.id;
                                    const isSaved = savedTabs[tab.id];

                                    return (
                                        <li className="nav-item" key={tab.id} style={{ marginRight: "2px" }}>
                                            <button
                                                type="button"
                                                onClick={() => setActiveTab(tab.id)}
                                                className={`nav-link ${
                                                    isActive
                                                        ? "bg-primary text-white font-weight-bold active"
                                                        : isSaved
                                                        ? "text-success font-weight-bold"
                                                        : "text-dark"
                                                }`}
                                                style={{
                                                    borderRadius: "6px 6px 0 0",
                                                    padding: "12px 18px",
                                                    border: isActive ? "1px solid #0d6efd" : "1px solid transparent",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "6px"
                                                }}
                                            >
                                                {tab.label}
                                                {isSaved && <span style={{ fontSize: "14px" }}>&#10003;</span>}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>

                        <div className="card-body p-4">
                            {/* TAB 1: PERSONAL (addmission_form.php) */}
                            {activeTab === "Personal" && (
                                <form onSubmit={(e) => { e.preventDefault(); handleNextTab("Personal", "Education"); }}>
                                    <div className="mb-3">
                                        <h4 className="card-title titleback">Personal Information :</h4>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 form-group">
                                            <label htmlFor="applicant_name">
                                                Enter Name (To be printed on Certificate) <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="applicant_name"
                                                name="applicant_name"
                                                className={`form-control ${errors.applicant_name ? "is-invalid" : ""}`}
                                                required
                                                placeholder="Full Name"
                                                value={formData.applicant_name}
                                                onChange={handleChange}
                                            />
                                            {errors.applicant_name && (
                                                <div className="invalid-feedback">{errors.applicant_name}</div>
                                            )}
                                        </div>

                                        <div className="col-md-6 form-group">
                                            <label htmlFor="id_card_name">Your short name mention on ID Card</label>
                                            <input
                                                type="text"
                                                id="id_card_name"
                                                name="id_card_name"
                                                className="form-control"
                                                placeholder="ID Card Name"
                                                value={formData.id_card_name}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-4 form-group">
                                            <label htmlFor="dob_dd">
                                                Date of Birth <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                id="dob_dd"
                                                name="dob_dd"
                                                className={`form-control ${errors.dob_dd ? "is-invalid" : ""}`}
                                                required
                                                max={maxDobDate}
                                                value={formData.dob_dd}
                                                onChange={handleChange}
                                            />
                                            {errors.dob_dd && <div className="invalid-feedback">{errors.dob_dd}</div>}
                                        </div>

                                        <div className="col-md-4 form-group">
                                            <label htmlFor="gender">
                                                Gender <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                id="gender"
                                                name="gender"
                                                className="form-control"
                                                value={formData.gender}
                                                onChange={handleChange}
                                            >
                                                <option value="Male">Male</option>
                                                <option value="Female">Female</option>
                                            </select>
                                        </div>

                                        <div className="col-md-4 form-group">
                                            <label htmlFor="email">
                                                Email <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                className={`form-control ${errors.email ? "is-invalid" : ""}`}
                                                required
                                                placeholder="Email Address"
                                                value={formData.email}
                                                onChange={handleChange}
                                            />
                                            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-3 form-group">
                                            <label htmlFor="mobile">
                                                Mobile No <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="tel"
                                                id="mobile"
                                                name="mobile"
                                                className={`form-control ${errors.mobile ? "is-invalid" : ""}`}
                                                required
                                                placeholder="Mobile Number"
                                                value={formData.mobile}
                                                onChange={handleChange}
                                                maxLength={10}
                                                inputMode="numeric"
                                            />
                                            {errors.mobile && <div className="invalid-feedback">{errors.mobile}</div>}
                                        </div>

                                        <div className="col-md-3 form-group">
                                            <label htmlFor="telephone">Telephone No</label>
                                            <input
                                                type="tel"
                                                id="telephone"
                                                name="telephone"
                                                className="form-control"
                                                placeholder="Phone Number"
                                                value={formData.telephone}
                                                onChange={handleChange}
                                                maxLength={10}
                                                inputMode="numeric"
                                            />
                                        </div>

                                        <div className="col-md-3 form-group">
                                            <label htmlFor="FemilyNo">Family No</label>
                                            <input
                                                type="tel"
                                                id="FemilyNo"
                                                name="FemilyNo"
                                                className={`form-control ${errors.FemilyNo ? "is-invalid" : ""}`}
                                                placeholder="Family Contact Number"
                                                value={formData.FemilyNo}
                                                onChange={handleChange}
                                                maxLength={10}
                                                inputMode="numeric"
                                            />
                                            {errors.FemilyNo && <div className="invalid-feedback">{errors.FemilyNo}</div>}
                                        </div>

                                        <div className="col-md-3 form-group">
                                            <label htmlFor="nationality">
                                                Nationality <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="nationality"
                                                name="nationality"
                                                className={`form-control ${errors.nationality ? "is-invalid" : ""}`}
                                                required
                                                placeholder="Nationality"
                                                value={formData.nationality}
                                                onChange={handleChange}
                                            />
                                            {errors.nationality && (
                                                <div className="invalid-feedback">{errors.nationality}</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Present Address */}
                                    <div className="mt-4 mb-3">
                                        <h4 className="card-title titleback">Present Address Details :</h4>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="present_address">
                                            Address <span className="text-danger">*</span>
                                        </label>
                                        <textarea
                                            id="present_address"
                                            name="present_address"
                                            rows="2"
                                            className={`form-control ${errors.present_address ? "is-invalid" : ""}`}
                                            required
                                            placeholder="Present Address"
                                            value={formData.present_address}
                                            onChange={handleChange}
                                        ></textarea>
                                        {errors.present_address && (
                                            <div className="invalid-feedback">{errors.present_address}</div>
                                        )}
                                    </div>

                                    <div className="row">
                                        <div className="col-md-3 form-group">
                                            <label htmlFor="present_city">
                                                City <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="present_city"
                                                name="present_city"
                                                className={`form-control ${errors.present_city ? "is-invalid" : ""}`}
                                                required
                                                placeholder="City"
                                                value={formData.present_city}
                                                onChange={handleChange}
                                            />
                                            {errors.present_city && (
                                                <div className="invalid-feedback">{errors.present_city}</div>
                                            )}
                                        </div>

                                        <div className="col-md-3 form-group">
                                            <label htmlFor="present_pin">
                                                Pin Code <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="present_pin"
                                                name="present_pin"
                                                className={`form-control ${errors.present_pin ? "is-invalid" : ""}`}
                                                required
                                                placeholder="Pin Code"
                                                value={formData.present_pin}
                                                onChange={handleChange}
                                            />
                                            {errors.present_pin && (
                                                <div className="invalid-feedback">{errors.present_pin}</div>
                                            )}
                                        </div>

                                        <div className="col-md-3 form-group">
                                            <label htmlFor="present_state">
                                                State <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="present_state"
                                                name="present_state"
                                                className={`form-control ${errors.present_state ? "is-invalid" : ""}`}
                                                required
                                                placeholder="State"
                                                value={formData.present_state}
                                                onChange={handleChange}
                                            />
                                            {errors.present_state && (
                                                <div className="invalid-feedback">{errors.present_state}</div>
                                            )}
                                        </div>

                                        <div className="col-md-3 form-group">
                                            <label htmlFor="present_country">
                                                Country <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="present_country"
                                                name="present_country"
                                                className={`form-control ${errors.present_country ? "is-invalid" : ""}`}
                                                required
                                                placeholder="Country"
                                                value={formData.present_country}
                                                onChange={handleChange}
                                            />
                                            {errors.present_country && (
                                                <div className="invalid-feedback">{errors.present_country}</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Helper: Same Address Checkbox */}
                                    <div className="form-check my-3">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="sameAddressCheck"
                                            checked={sameAddress}
                                            onChange={handleSameAddressToggle}
                                        />
                                        <label className="form-check-label font-weight-bold" htmlFor="sameAddressCheck">
                                            Permanent Address is the same as Present Address
                                        </label>
                                    </div>

                                    {/* Permanent Address */}
                                    <div className="mt-4 mb-3">
                                        <h4 className="card-title titleback">Permanent Address Details :</h4>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="permanent_address">
                                            Address <span className="text-danger">*</span>
                                        </label>
                                        <textarea
                                            id="permanent_address"
                                            name="permanent_address"
                                            rows="2"
                                            className={`form-control ${errors.permanent_address ? "is-invalid" : ""}`}
                                            required
                                            placeholder="Permanent Address"
                                            value={formData.permanent_address}
                                            onChange={handleChange}
                                        ></textarea>
                                        {errors.permanent_address && (
                                            <div className="invalid-feedback">{errors.permanent_address}</div>
                                        )}
                                    </div>

                                    <div className="row">
                                        <div className="col-md-3 form-group">
                                            <label htmlFor="permanent_city">
                                                City <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="permanent_city"
                                                name="permanent_city"
                                                className={`form-control ${errors.permanent_city ? "is-invalid" : ""}`}
                                                required
                                                placeholder="City"
                                                value={formData.permanent_city}
                                                onChange={handleChange}
                                            />
                                            {errors.permanent_city && (
                                                <div className="invalid-feedback">{errors.permanent_city}</div>
                                            )}
                                        </div>

                                        <div className="col-md-3 form-group">
                                            <label htmlFor="permanent_pin">
                                                Pin Code <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="permanent_pin"
                                                name="permanent_pin"
                                                className={`form-control ${errors.permanent_pin ? "is-invalid" : ""}`}
                                                required
                                                placeholder="Pin Code"
                                                value={formData.permanent_pin}
                                                onChange={handleChange}
                                            />
                                            {errors.permanent_pin && (
                                                <div className="invalid-feedback">{errors.permanent_pin}</div>
                                            )}
                                        </div>

                                        <div className="col-md-3 form-group">
                                            <label htmlFor="permanent_state">
                                                State <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="permanent_state"
                                                name="permanent_state"
                                                className={`form-control ${errors.permanent_state ? "is-invalid" : ""}`}
                                                required
                                                placeholder="State"
                                                value={formData.permanent_state}
                                                onChange={handleChange}
                                            />
                                            {errors.permanent_state && (
                                                <div className="invalid-feedback">{errors.permanent_state}</div>
                                            )}
                                        </div>

                                        <div className="col-md-3 form-group">
                                            <label htmlFor="permanent_country">
                                                Country <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="permanent_country"
                                                name="permanent_country"
                                                className={`form-control ${errors.permanent_country ? "is-invalid" : ""}`}
                                                required
                                                placeholder="Country"
                                                value={formData.permanent_country}
                                                onChange={handleChange}
                                            />
                                            {errors.permanent_country && (
                                                <div className="invalid-feedback">{errors.permanent_country}</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Action Button for Tab 1 */}
                                    <div className="d-flex justify-content-end mt-4">
                                        <button type="submit" className="btn btn-primary btn-lg">
                                            Next &rarr;
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* TAB 2: EDUCATION (education_form.php) */}
                            {activeTab === "Education" && (
                                <form onSubmit={(e) => { e.preventDefault(); handleNextTab("Education", "Work"); }}>
                                    <div className="mb-3">
                                        <h4 className="card-title titleback">Academic Qualification :</h4>
                                    </div>

                                    <div className="row">
                                        {/* Degree */}
                                        <div className="col-md-6 form-group">
                                            <label htmlFor="degree">
                                                Degree <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                id="degree"
                                                name="degree"
                                                className={`form-control ${errors.degree ? "is-invalid" : ""}`}
                                                required
                                                value={formData.degree}
                                                onChange={handleChange}
                                            >
                                                <option value="">Please select</option>
                                                {educations.map((item) => (
                                                    <option key={item.Id || item.id} value={item.Id || item.id}>
                                                        {item.Education}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.degree && <div className="invalid-feedback">{errors.degree}</div>}
                                        </div>

                                        {/* Discipline */}
                                        <div className="col-md-6 form-group">
                                            <label htmlFor="discipline">
                                                Select Discipline <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                id="discipline"
                                                name="discipline"
                                                className={`form-control ${errors.discipline ? "is-invalid" : ""}`}
                                                required
                                                value={formData.discipline}
                                                onChange={handleChange}
                                            >
                                                <option value="">Please select</option>
                                                {disciplines.map((item) => (
                                                    <option key={item.Id || item.id} value={item.Id || item.id}>
                                                        {item.Deciplin || item.discipline}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.discipline && (
                                                <div className="invalid-feedback">{errors.discipline}</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="row">
                                        {/* College */}
                                        <div className="col-md-6 form-group">
                                            <label htmlFor="college">
                                                Select College <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                id="college"
                                                name="college"
                                                className={`form-control ${errors.college ? "is-invalid" : ""}`}
                                                required
                                                value={formData.college}
                                                onChange={handleCollegeChange}
                                            >
                                                <option value="">Please select</option>
                                                {colleges.map((col) => (
                                                    <option key={col.id} value={col.id}>
                                                        {col.college_name}
                                                    </option>
                                                ))}
                                                <option value="ADDTHIS">+ Add New College</option>
                                            </select>
                                            {errors.college && <div className="invalid-feedback">{errors.college}</div>}

                                            {showNewCollegeInput && (
                                                <div className="mt-2">
                                                    <label htmlFor="college_data_value">Enter New College Name</label>
                                                    <input
                                                        type="text"
                                                        id="college_data_value"
                                                        name="college_data_value"
                                                        className={`form-control ${errors.college_data_value ? "is-invalid" : ""}`}
                                                        placeholder="College Name"
                                                        value={formData.college_data_value}
                                                        onChange={handleChange}
                                                    />
                                                    {errors.college_data_value && (
                                                        <div className="invalid-feedback">{errors.college_data_value}</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* University */}
                                        <div className="col-md-6 form-group">
                                            <label htmlFor="university">
                                                University <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="university"
                                                name="university"
                                                className={`form-control ${errors.university ? "is-invalid" : ""}`}
                                                required
                                                placeholder="University"
                                                value={formData.university}
                                                onChange={handleChange}
                                            />
                                            {errors.university && (
                                                <div className="invalid-feedback">{errors.university}</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="row">
                                        {/* Year of Passing */}
                                        <div className="col-md-4 form-group">
                                            <label htmlFor="year_of_passing">
                                                Year Of Passing <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="year_of_passing"
                                                name="year_of_passing"
                                                className={`form-control ${errors.year_of_passing ? "is-invalid" : ""}`}
                                                required
                                                placeholder="e.g. 2024"
                                                value={formData.year_of_passing}
                                                onChange={handleChange}
                                            />
                                            {errors.year_of_passing && (
                                                <div className="invalid-feedback">{errors.year_of_passing}</div>
                                            )}
                                        </div>

                                        {/* Marks (in %) */}
                                        <div className="col-md-4 form-group">
                                            <label htmlFor="marks">
                                                Marks (in %) <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                id="marks"
                                                name="marks"
                                                className={`form-control ${errors.marks ? "is-invalid" : ""}`}
                                                required
                                                placeholder="Percentage"
                                                value={formData.marks}
                                                onChange={handleChange}
                                            />
                                            {errors.marks && <div className="invalid-feedback">{errors.marks}</div>}
                                        </div>

                                        {/* Status */}
                                        <div className="col-md-4 form-group">
                                            <label htmlFor="status">
                                                Status <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                id="status"
                                                name="status"
                                                className={`form-control ${errors.status ? "is-invalid" : ""}`}
                                                required
                                                value={formData.status}
                                                onChange={handleChange}
                                            >
                                                <option value="">Select</option>
                                                <option value="appeared">Appeared</option>
                                                <option value="pass">Pass</option>
                                                <option value="kt">KT</option>
                                            </select>
                                            {errors.status && <div className="invalid-feedback">{errors.status}</div>}
                                        </div>
                                    </div>

                                    <div className="row">
                                        {/* Total Backlogs */}
                                        <div className="col-md-6 form-group">
                                            <label htmlFor="backlogs">
                                                Total number of Backlogs / Arrears <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                id="backlogs"
                                                name="backlogs"
                                                className={`form-control ${errors.backlogs ? "is-invalid" : ""}`}
                                                required
                                                placeholder="0"
                                                value={formData.backlogs}
                                                onChange={handleChange}
                                            />
                                            {errors.backlogs && <div className="invalid-feedback">{errors.backlogs}</div>}
                                        </div>

                                        {/* Remark */}
                                        <div className="col-md-6 form-group">
                                            <label htmlFor="remark">Remark</label>
                                            <textarea
                                                id="remark"
                                                name="remark"
                                                rows="2"
                                                className="form-control"
                                                placeholder="Any remark"
                                                value={formData.remark}
                                                onChange={handleChange}
                                            ></textarea>
                                        </div>
                                    </div>

                                    {/* Action Buttons for Tab 2 */}
                                    <div className="d-flex justify-content-between mt-4">
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-lg"
                                            onClick={() => setActiveTab("Personal")}
                                        >
                                            &larr; Previous
                                        </button>
                                        <button type="submit" className="btn btn-primary btn-lg">
                                            Next &rarr;
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* TAB 3: WORK (work.php) */}
                            {activeTab === "Work" && (
                                <form onSubmit={(e) => { e.preventDefault(); handleNextTab("Work", "Training Programme Selection"); }}>
                                    <div className="mb-3">
                                        <h4 className="card-title titleback">Occupational Status :</h4>
                                    </div>

                                    <div className="form-group col-md-6 p-0 mb-4">
                                        <label htmlFor="Occupation">
                                            Occupation <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            id="Occupation"
                                            name="Occupation"
                                            className={`form-control ${errors.Occupation ? "is-invalid" : ""}`}
                                            required
                                            value={formData.Occupation}
                                            onChange={handleChange}
                                        >
                                            <option value="1">Student</option>
                                            <option value="2">Employee</option>
                                            <option value="3">Self Employee</option>
                                        </select>
                                        {errors.Occupation && <div className="invalid-feedback">{errors.Occupation}</div>}
                                    </div>

                                    {/* Employee Section */}
                                    {formData.Occupation === "2" && (
                                        <div className="p-3 border rounded mb-4" style={{ backgroundColor: "#fdfdfd" }}>
                                            <h5 className="titleback mb-3 font-weight-bold">Current Job Data Information :</h5>

                                            <div className="row">
                                                <div className="col-md-6 form-group">
                                                    <label htmlFor="company_name">
                                                        Job Organisation <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="company_name"
                                                        name="company_name"
                                                        className={`form-control ${errors.company_name ? "is-invalid" : ""}`}
                                                        placeholder="Company Name"
                                                        value={formData.company_name}
                                                        onChange={handleChange}
                                                    />
                                                    {errors.company_name && (
                                                        <div className="invalid-feedback">{errors.company_name}</div>
                                                    )}
                                                </div>

                                                <div className="col-md-6 form-group">
                                                    <label htmlFor="discription">Description / Nature of Business</label>
                                                    <input
                                                        type="text"
                                                        id="discription"
                                                        name="discription"
                                                        className="form-control"
                                                        placeholder="Description"
                                                        value={formData.discription}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6 form-group">
                                                    <label htmlFor="designation">
                                                        Designation <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="designation"
                                                        name="designation"
                                                        className={`form-control ${errors.designation ? "is-invalid" : ""}`}
                                                        placeholder="Designation"
                                                        value={formData.designation}
                                                        onChange={handleChange}
                                                    />
                                                    {errors.designation && (
                                                        <div className="invalid-feedback">{errors.designation}</div>
                                                    )}
                                                </div>

                                                <div className="col-md-3 form-group">
                                                    <label htmlFor="working_years">Working From - Years</label>
                                                    <select
                                                        id="working_years"
                                                        name="working_years"
                                                        className="form-control"
                                                        value={formData.working_years}
                                                        onChange={handleChange}
                                                    >
                                                        {Array.from({ length: 51 }, (_, i) => (
                                                            <option key={i} value={i}>
                                                                {i}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="col-md-3 form-group">
                                                    <label htmlFor="working_months">Working From - Months</label>
                                                    <select
                                                        id="working_months"
                                                        name="working_months"
                                                        className="form-control"
                                                        value={formData.working_months}
                                                        onChange={handleChange}
                                                    >
                                                        {Array.from({ length: 12 }, (_, i) => (
                                                            <option key={i} value={i}>
                                                                {i}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Self Employee Section */}
                                    {formData.Occupation === "3" && (
                                        <div className="p-3 border rounded mb-4" style={{ backgroundColor: "#fdfdfd" }}>
                                            <h5 className="titleback mb-3 font-weight-bold">Self Employee Information :</h5>

                                            <div className="form-group">
                                                <label htmlFor="self_employment_details">
                                                    Details of Self Employment <span className="text-danger">*</span>
                                                </label>
                                                <textarea
                                                    id="self_employment_details"
                                                    name="self_employment_details"
                                                    rows="3"
                                                    className={`form-control ${
                                                        errors.self_employment_details ? "is-invalid" : ""
                                                    }`}
                                                    placeholder="Details of Self Employment"
                                                    value={formData.self_employment_details}
                                                    onChange={handleChange}
                                                ></textarea>
                                                {errors.self_employment_details && (
                                                    <div className="invalid-feedback">{errors.self_employment_details}</div>
                                                )}
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6 form-group">
                                                    <label htmlFor="sworking_years">Working From - Years</label>
                                                    <select
                                                        id="sworking_years"
                                                        name="sworking_years"
                                                        className="form-control"
                                                        value={formData.sworking_years}
                                                        onChange={handleChange}
                                                    >
                                                        {Array.from({ length: 51 }, (_, i) => (
                                                            <option key={i} value={i}>
                                                                {i}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="col-md-6 form-group">
                                                    <label htmlFor="sworking_months">Working From - Months</label>
                                                    <select
                                                        id="sworking_months"
                                                        name="sworking_months"
                                                        className="form-control"
                                                        value={formData.sworking_months}
                                                        onChange={handleChange}
                                                    >
                                                        {Array.from({ length: 12 }, (_, i) => (
                                                            <option key={i} value={i}>
                                                                {i}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Student Info Alert */}
                                    {formData.Occupation === "1" && (
                                        <div className="alert alert-info">
                                            <strong>Student:</strong> No prior work experience details required. Click Next to proceed.
                                        </div>
                                    )}

                                    {/* Action Buttons for Tab 3 */}
                                    <div className="d-flex justify-content-between mt-4">
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-lg"
                                            onClick={() => setActiveTab("Education")}
                                        >
                                            &larr; Previous
                                        </button>
                                        <button type="submit" className="btn btn-primary btn-lg">
                                            Next &rarr;
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* TAB 4: TRAINING PROGRAMME SELECTION (Programme.php) */}
                            {activeTab === "Training Programme Selection" && (
                                <form onSubmit={(e) => { e.preventDefault(); handleNextTab("Training Programme Selection", "Documents"); }}>
                                    <div className="mb-3">
                                        <h4 className="card-title titleback">Training Programme :</h4>
                                    </div>

                                    {/* Select Training Programme */}
                                    <div className="form-group mb-4">
                                        <label htmlFor="training_programme">
                                            Select Training Programme <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            id="training_programme"
                                            name="training_programme"
                                            className={`form-control ${errors.training_programme ? "is-invalid" : ""}`}
                                            required
                                            value={formData.training_programme || formData.Course_Id}
                                            onChange={handleChange}
                                        >
                                            <option value="">Please select</option>
                                            {(Array.isArray(courses) ? courses : []).filter((c) => c && c.IsDelete != 1 && c.isDelete != 1 && c.deleted != 1).map((course) => (
                                                <option key={course.Course_Id} value={course.Course_Id}>
                                                    {course.Course_Name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.training_programme && (
                                            <div className="invalid-feedback">{errors.training_programme}</div>
                                        )}
                                    </div>

                                    <div className="row">
                                        {/* Select Category */}
                                        <div className="col-md-6 form-group">
                                            <label htmlFor="category">
                                                Select Category <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                id="category"
                                                name="category"
                                                className={`form-control ${errors.category ? "is-invalid" : ""}`}
                                                required
                                                value={formData.category || formData.category_id}
                                                onChange={handleChange}
                                            >
                                                <option value="">Select category</option>
                                                {batchCategories.length > 0 ? (
                                                    batchCategories.map((c) => (
                                                        <option key={c.id} value={c.id}>
                                                            {c.BatchCategory}
                                                        </option>
                                                    ))
                                                ) : (
                                                    <>
                                                        <option value="1">Full Time</option>
                                                        <option value="2">Part Time</option>
                                                        <option value="3">Corporate Training</option>
                                                        <option value="4">Weekend Batches</option>
                                                        <option value="5">Transfer</option>
                                                        <option value="6">ONLINE</option>
                                                    </>
                                                )}
                                            </select>
                                            {errors.category && (
                                                <div className="invalid-feedback">{errors.category}</div>
                                            )}
                                        </div>

                                        {/* Select Batch Code */}
                                        <div className="col-md-6 form-group">
                                            <label htmlFor="Batch_code">
                                                Select Batch Code <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                id="Batch_code"
                                                name="Batch_code"
                                                className={`form-control ${errors.Batch_code ? "is-invalid" : ""}`}
                                                required
                                                value={formData.Batch_code}
                                                onChange={handleBatchChange}
                                            >
                                                <option value="">Select Batch Code</option>
                                                {filteredBatches.map((b) => (
                                                    <option key={b.Batch_Id} value={b.Batch_code}>
                                                        {b.Batch_code}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.Batch_code && (
                                                <div className="invalid-feedback">{errors.Batch_code}</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Fees & Payment Info */}
                                    <div className="row">
                                        <div className="col-md-4 form-group">
                                            <label htmlFor="Amount">Batch Fee Amount (INR)</label>
                                            <input
                                                type="text"
                                                id="Amount"
                                                name="Amount"
                                                className="form-control"
                                                placeholder="Total Fees"
                                                value={formData.Amount}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="col-md-4 form-group">
                                            <label htmlFor="Payment_Type">Payment Type</label>
                                            <select
                                                id="Payment_Type"
                                                name="Payment_Type"
                                                className="form-control"
                                                value={formData.Payment_Type}
                                                onChange={handleChange}
                                            >
                                                <option value="Lumpsum">Lumpsum</option>
                                                <option value="Installment">Installment</option>
                                            </select>
                                        </div>

                                        <div className="col-md-4 form-group">
                                            <label htmlFor="Admission_Dt">Admission Date</label>
                                            <input
                                                type="date"
                                                id="Admission_Dt"
                                                name="Admission_Dt"
                                                className="form-control"
                                                value={formData.Admission_Dt}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>

                                    {/* Terms & Conditions Checkbox */}
                                    <div className="form-check my-4">
                                        <input
                                            type="checkbox"
                                            className={`form-check-input ${errors.terms_conditions ? "is-invalid" : ""}`}
                                            id="terms_conditions"
                                            name="terms_conditions"
                                            checked={formData.terms_conditions}
                                            onChange={handleChange}
                                            required
                                        />
                                        <label className="form-check-label font-weight-bold" htmlFor="terms_conditions">
                                            I read and agree to all{" "}
                                            <a
                                                href="https://suvidya.ac.in/regulation/"
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-primary text-decoration-underline"
                                            >
                                                terms &amp; conditions
                                            </a>{" "}
                                            <span className="text-danger">*</span>
                                        </label>
                                        {errors.terms_conditions && (
                                            <div className="invalid-feedback d-block">{errors.terms_conditions}</div>
                                        )}
                                    </div>

                                    {/* Action Buttons for Tab 4 */}
                                    <div className="d-flex justify-content-between mt-4">
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-lg"
                                            onClick={() => setActiveTab("Work")}
                                        >
                                            &larr; Previous
                                        </button>
                                        <button type="submit" className="btn btn-primary btn-lg">
                                            Next &rarr;
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* TAB 5: DOCUMENTS (documents.php) - FINAL STEP */}
                            {activeTab === "Documents" && (
                                <form onSubmit={handleFinalSubmit}>
                                    <div className="mb-3">
                                        <h4 className="card-title titleback">Document Upload :</h4>
                                        <p className="text-muted small">
                                            Upload student identity and academic documents. File size must be less than 1MB per document.
                                        </p>
                                    </div>

                                    <div className="row">
                                        {/* Photo Upload */}
                                        <div className="col-md-4 form-group">
                                            <label htmlFor="photo" className="font-weight-bold">
                                                Photo <span className="text-danger small">(less than 1MB)</span>
                                            </label>
                                            <input
                                                type="file"
                                                id="photo"
                                                name="photo"
                                                className="form-control"
                                                accept=".jpeg, .jpg, .png, .gif, .bmp, .webp"
                                                onChange={(e) => handleFileChange(e, "photo")}
                                            />
                                            {uploadStatus.photo && (
                                                <small className="text-success d-block mt-1 font-weight-bold">
                                                    &#10003; {documentFiles.photo?.name} ready for upload
                                                </small>
                                            )}
                                        </div>

                                        {/* Certificate Upload */}
                                        <div className="col-md-4 form-group">
                                            <label htmlFor="certificate" className="font-weight-bold">
                                                Certificate <span className="text-danger small">(less than 1MB)</span>
                                            </label>
                                            <input
                                                type="file"
                                                id="certificate"
                                                name="certificate"
                                                className="form-control"
                                                accept=".jpeg, .jpg, .png, .pdf, .webp"
                                                onChange={(e) => handleFileChange(e, "certificate")}
                                            />
                                            {uploadStatus.certificate && (
                                                <small className="text-success d-block mt-1 font-weight-bold">
                                                    &#10003; {documentFiles.certificate?.name} ready for upload
                                                </small>
                                            )}
                                        </div>

                                        {/* Marksheet Upload */}
                                        <div className="col-md-4 form-group">
                                            <label htmlFor="marksheet" className="font-weight-bold">
                                                Marksheet <span className="text-danger small">(less than 1MB)</span>
                                            </label>
                                            <input
                                                type="file"
                                                id="marksheet"
                                                name="marksheet"
                                                className="form-control"
                                                accept=".jpeg, .jpg, .png, .pdf, .webp"
                                                onChange={(e) => handleFileChange(e, "marksheet")}
                                            />
                                            {uploadStatus.marksheet && (
                                                <small className="text-success d-block mt-1 font-weight-bold">
                                                    &#10003; {documentFiles.marksheet?.name} ready for upload
                                                </small>
                                            )}
                                        </div>
                                    </div>

                                    <hr className="my-3" />

                                    {/* Proof Sections */}
                                    <div className="row">
                                        {/* Photo ID Proof Column */}
                                        <div className="col-md-6 border-right pr-4">
                                            <h5 className="font-weight-bold mb-3 text-secondary">Photo ID Proof</h5>
                                            <div className="form-group">
                                                <label htmlFor="photo_id_proof">Photo ID Proof Type</label>
                                                <select
                                                    id="photo_id_proof"
                                                    name="photo_id_proof"
                                                    className="form-control"
                                                    value={formData.photo_id_proof}
                                                    onChange={handleChange}
                                                >
                                                    <option value="">Select ID Type</option>
                                                    <option value="passport">Passport</option>
                                                    <option value="pen">PanCard</option>
                                                    <option value="aadhar">Aadhar Card</option>
                                                    <option value="driving_licence">Driving Licence</option>
                                                    <option value="voter_id">Voter ID</option>
                                                </select>
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="photo_id_proof_file">
                                                    Photo ID Proof File <span className="text-danger small">(less than 1MB)</span>
                                                </label>
                                                <input
                                                    type="file"
                                                    id="photo_id_proof_file"
                                                    name="photo_id_proof_file"
                                                    className="form-control"
                                                    accept=".jpeg, .jpg, .png, .pdf, .webp"
                                                    onChange={(e) => handleFileChange(e, "photo_id_proof_file")}
                                                />
                                                {uploadStatus.photo_id_proof_file && (
                                                    <small className="text-success d-block mt-1 font-weight-bold">
                                                        &#10003; {documentFiles.photo_id_proof_file?.name} ready
                                                    </small>
                                                )}
                                            </div>
                                        </div>

                                        {/* Address Proof Column */}
                                        <div className="col-md-6 pl-4">
                                            <h5 className="font-weight-bold mb-3 text-secondary">Address Proof</h5>
                                            <div className="form-group">
                                                <label htmlFor="address_proof">Address Proof Type</label>
                                                <select
                                                    id="address_proof"
                                                    name="address_proof"
                                                    className="form-control"
                                                    value={formData.address_proof}
                                                    onChange={handleChange}
                                                >
                                                    <option value="">Select Proof Type</option>
                                                    <option value="electricity_bill">Electricity Bill</option>
                                                    <option value="rent_agreement">Rent Agreement</option>
                                                    <option value="passport">Passport</option>
                                                    <option value="pen">PanCard</option>
                                                    <option value="aadhar">Aadhar Card</option>
                                                    <option value="driving_licence">Driving Licence</option>
                                                    <option value="voter_id">Voter ID</option>
                                                </select>
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="address_proof_file">
                                                    Address Proof File <span className="text-danger small">(less than 1MB)</span>
                                                </label>
                                                <input
                                                    type="file"
                                                    id="address_proof_file"
                                                    name="address_proof_file"
                                                    className="form-control"
                                                    accept=".jpeg, .jpg, .png, .pdf, .webp"
                                                    onChange={(e) => handleFileChange(e, "address_proof_file")}
                                                />
                                                {uploadStatus.address_proof_file && (
                                                    <small className="text-success d-block mt-1 font-weight-bold">
                                                        &#10003; {documentFiles.address_proof_file?.name} ready
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons for Tab 5 */}
                                    <div className="d-flex justify-content-between mt-4">
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-lg"
                                            onClick={() => setActiveTab("Training Programme Selection")}
                                        >
                                            &larr; Previous
                                        </button>
                                        <button type="submit" className="btn btn-success btn-lg font-weight-bold">
                                            Submit Admission &amp; Documents &#10003;
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>

                    {/* Success Confirmation Modal */}
                    {successModal && (
                        <div
                            className="modal fade show"
                            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.6)" }}
                            tabIndex="-1"
                            role="dialog"
                        >
                            <div className="modal-dialog modal-dialog-centered" role="document">
                                <div className="modal-content text-center p-4" style={{ borderRadius: "12px" }}>
                                    <div className="mb-3">
                                        <div
                                            style={{
                                                width: "70px",
                                                height: "70px",
                                                borderRadius: "50%",
                                                backgroundColor: "#d4edda",
                                                color: "#28a745",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "36px"
                                            }}
                                        >
                                            &#10003;
                                        </div>
                                    </div>
                                    <h4 className="modal-title font-weight-bold text-success mb-2">
                                        Admission Successfully Completed!
                                    </h4>
                                    <p className="text-muted">
                                        The student admission record has been created and registered in the database.
                                    </p>

                                    <div className="bg-light p-3 rounded mb-3 text-left">
                                        <p className="mb-1">
                                            <strong>Student Name:</strong> {successModal.student_name}
                                        </p>
                                        <p className="mb-1">
                                            <strong>Student Code:</strong>{" "}
                                            <span className="badge badge-primary" style={{ fontSize: "14px" }}>
                                                {successModal.student_code}
                                            </span>
                                        </p>
                                        <p className="mb-1">
                                            <strong>Course:</strong> {successModal.course}
                                        </p>
                                        <p className="mb-1">
                                            <strong>Batch Code:</strong> {successModal.batch}
                                        </p>
                                        <p className="mb-0">
                                            <strong>Student ID:</strong> {successModal.student_id}
                                        </p>
                                    </div>

                                    <div className="d-flex justify-content-center gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-primary mr-2"
                                            onClick={() => navigate("/onlineadmission")}
                                        >
                                            View Online Admissions
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() => {
                                                setSuccessModal(null);
                                                window.location.reload();
                                            }}
                                        >
                                            Add Another Admission
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManualAdmission;
